import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPotCollectionActive,
  selectPotCollectionEntryPhase,
  selectPotCollectionCoinCount,
  selectPotCollectionRespinsLeft,
  selectPotCollectionTransitionActive,
  selectPotCollectionReloadPhase,
  selectFeatureSpinComplete,
  selectFeatureSpinActive,
  selectPendingAutoPlaySpeed,
  selectAutoPlaySpeed,
  selectIntroPopupActive,
  selectInfoPopupActive,
  selectErrorPopupActive,
  selectErrorRecoveryTransitionActive,
} from "../State/selectors";
import { selectShowPotCollectionReload } from "../State/Slices/animationsSlice";
import {
  setPotCollectionEntryPhase,
  setPotCollectionEjectionComplete,
  setPotCollectionCurrentActivation,
  setPotCollectionFeatureApplicationComplete,
  incrementPotCollectionCoinCount,
  setPotCollectionActive,
  clearFeatureStateToBase,
  refreshGameData,
  setCriticalAnimationPlaying,
  setUserInterfaceDisabled,
  setFeatureSpinComplete,
  setFeatureSpinActive,
  setPotCollectionTransitionActive,
  applyPendingAutoplaySpeed,
  setAutoplaySpeedNoSave,
  setShowPotCollectionGrandJackpot,
} from "../State/actions";
import store from "../State/Store/store";
import { useReelRefs } from "./ReelRefsContext";
import { useDimensions } from "./DimensionsContext";
// import { GAME_LAYOUT } from "../settings";              // TODO: will be needed later
import PotCollectionOrbActivator from "../Games/PotCollection/PotCollectionOrbActivator";
import PotCollectionSpinEngine from "../Games/PotCollection/PotCollectionSpinEngine";
import PotCollectionScreenProgressProcessor from "../Games/PotCollection/PotCollectionScreenProgressProcessor";
import { isTurboLike } from "../utils/speedHelpers";
import { createPlayButtonRotationAnimation } from "../utilities";
import anime from "animejs";
import TickerTimeout from "../utils/TickerTimeout";
import StorageManager from "../utils/StorageManager";
import { useAppActivity } from "./AppActivityContext";
import { FEATURES_CONFIG } from "../featuresConfig";

// Map feature letter to screen_progress action prefixes.
// Used to filter the trigger spin's screen_progress by feature type.
const FEATURE_TO_ACTIONS = {
  M: ["m_reveals"],
  D: ["d_doubles", "d_reveals"],
  C: ["c_collect"],
};

/**
 * PotCollectionControlContext
 *
 * Orchestrates the Pot Collection (Zeus Respins) bonus game:
 * - Cell-based spin engine (blanks only, N=1, no blur)
 * - Cascading stops with speed-dependent delays
 * - Event-driven spin scheduling (to be added)
 * - Entry phase progression callbacks
 *
 * Architecture: zeus_respins_architecture_analysis.md §4–§9
 * Pattern: Mirrors RoyalCollectControlContext structure.
 *
 * Story 9.1: Shell only — spin/stop/collection logic added in Stories 9.3–9.6.
 */

export const PotCollectionControlContext = createContext(null);

export const PotCollectionControlContextProvider = ({ children }) => {
  const {
    potCollectionReelRefs,
    potCollectionOverlayReelRefs,
    potCollectionCollectionLayerRef,
    potCollectionRespinCounterRef,
    potCollectionCoinCounterRef,
    playButtonRef,
    potChargingAnimatorRef,
    gameViewControllerRef,
  } = useReelRefs();

  const { symbolWidth, symbolHeight, reelSpacing } = useDimensions();

  const dispatch = useDispatch();
  const { isActive } = useAppActivity();

  // ── Spin tracking refs (RC pattern) ──
  const spinningCountRef = useRef(0);
  const completedCountRef = useRef(0);
  const isSpinningRef = useRef(false);
  const autoStopTimeoutRef = useRef(null);
  const stopTimeoutsRef = useRef(new Map());
  const coinLandedThisSpinRef = useRef(false);

  // Component readiness tracking
  const componentsEverReadyRef = useRef(false);

  // OrbActivator instance ref
  const orbActivatorRef = useRef(null);

  // SpinEngine instance ref
  const spinEngineRef = useRef(null);

  // ── Autoplay ──
  const autoplayTimeoutRef = useRef(null);
  const spinLoopStartedRef = useRef(false); // Prevents duplicate 'first spin' triggers
  const autoplayStartedRef = useRef(false); // True after user clicks play to start spin loop
  const pulseIntervalRef = useRef(null);    // Pulse glow animation interval on play button

  // Entry-phase feature animating flag (before SpinEngine exists)
  const entryFeatureAnimatingRef = useRef(false);
  // Tracks slam stop requests that arrive before spawn phase starts
  const entrySlamStopRef = useRef(false);
  // Tracks that entry sequence is waiting for transition to finish
  const entryWaitingForTransitionRef = useRef(false);

  // Tab-switch pause/resume state (ported from ReelControlContext)
  const pausedByInactivityRef = useRef(false);
  const lastTabReturnTimeRef = useRef(null);

  // Redux selectors
  const potCollectionActive = useSelector(selectPotCollectionActive);
  const entryPhase = useSelector(selectPotCollectionEntryPhase);
  // const symbolsReady = useSelector(selectPotCollectionSymbolsReady);      // TODO: will be needed later
  // const reelsReady = useSelector(selectPotCollectionReelsReady);           // TODO: will be needed later
  // const overlayReelsReady = useSelector(selectPotCollectionOverlayReelsReady); // TODO: will be needed later
  // const bet = useSelector(selectBet);                                      // TODO: will be needed later
  const coinCount = useSelector(selectPotCollectionCoinCount);
  const respinsLeft = useSelector(selectPotCollectionRespinsLeft);
  const transitionActive = useSelector(selectPotCollectionTransitionActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const showReloadPopup = useSelector(selectShowPotCollectionReload);
  const reloadPhase = useSelector(selectPotCollectionReloadPhase);
  const errorPopupActive = useSelector(selectErrorPopupActive);
  const errorRecoveryTransitionActive = useSelector(selectErrorRecoveryTransitionActive);

  // ── Cleanup on unmount ──
  // Clear all timeouts when context provider unmounts
  React.useEffect(() => {
    const autoStop = autoStopTimeoutRef;
    const stops = stopTimeoutsRef;
    return () => {
        if (autoStop.current && typeof autoStop.current.clear === "function") autoStop.current.clear();
      stops.current.forEach((id) => { if (id && typeof id.clear === "function") id.clear(); });
      stops.current.clear();
    };
  }, []);

  // ── Apply Pending Autoplay Speed (mirrors H&W pattern) ──
  // When turbo is toggled mid-spin, the speed change is queued as pendingAutoPlaySpeed.
  // Apply it immediately so _isFastMode() picks it up at every reactive check point.
  // If switching TO turbo: slam stop current animation + speed up all remaining phases.
  useEffect(() => {
    if (potCollectionActive && pendingAutoPlaySpeed) {
      // Apply speed change immediately so _isFastMode() picks it up
      // at the next feature boundary (reactive per-feature check).

      dispatch(applyPendingAutoplaySpeed());

      // Switching TO turbo → slam stop current + speed up everything
      if (isTurboLike(pendingAutoPlaySpeed)) {
        const engine = spinEngineRef.current;
        if (engine) {
          // Slam stop feature animations (forwards to OrbActivator + EOB if active)
          // Only if SLAM_STOP_BUTTON_ENABLED — otherwise features run at config-driven speeds
          if (FEATURES_CONFIG.POT_COLLECTION.FEATURE_SPEED?.SLAM_STOP_BUTTON_ENABLED) {
            engine.requestSlamStop();
          }

          // If reels are still spinning (not yet in feature window), stop them immediately.
          // This gives the same result as the player pressing the stop button.
          if (engine._isSpinning && !engine._featureAnimating) {

            engine.handleManualStop();
          }
        }

        // Entry sequence: slam stop OrbActivator (covers coin spawn + feature activation)
        // Always allowed — entry spawn should be skippable regardless of SLAM_STOP_BUTTON_ENABLED
        if (entryFeatureAnimatingRef.current && orbActivatorRef.current) {

          orbActivatorRef.current.requestSlamStop();
        }

        // Speed up inter-spin delay: if an autoplay timeout is pending,
        // clear it and fire the next spin immediately (turbo = no waiting).
        if (autoplayTimeoutRef.current) {
          if (autoplayTimeoutRef.current && typeof autoplayTimeoutRef.current.clear === "function") autoplayTimeoutRef.current.clear();
          autoplayTimeoutRef.current = null;
          // Fire next spin immediately if engine exists and is idle.
          // respinsLeft is checked here for the same reason the main loop
          // below checks it — _isSpinning and _endOfBonusActive are both
          // false during the async tail of the previous spin, so they do not
          // prove the bonus is still running. The engine now checks too; this
          // stays so the shortcut matches the loop it is short-cutting.
          const respinsRemaining = store.getState().potCollection?.respinsLeft ?? 0;
          if (engine && !engine._isSpinning && !engine._endOfBonusActive && respinsRemaining > 0) {

            engine.handleSpin();
          }
        }
      }
      // Switching FROM turbo → no slam stop needed.
      // Current feature finishes at its current pace,
      // next feature reads _isFastMode() and runs at normal speed.
    }
  }, [potCollectionActive, pendingAutoPlaySpeed, featureSpinComplete, dispatch]);

  // ── Direct speed change during EOB or entry (non-pending path) ──
  // During EOB and entry, featureSpinActive is false and autoPlay may be inactive,
  // so ButtonTurbo dispatches setAutoplaySpeed directly (not setPendingAutoplaySpeed).
  // This effect catches that direct change and slams the active phase.
  const prevAutoPlaySpeedRef = useRef(autoPlaySpeed);
  useEffect(() => {
    const prev = prevAutoPlaySpeedRef.current;
    prevAutoPlaySpeedRef.current = autoPlaySpeed;

    if (!potCollectionActive) return;
    // Only react when switching TO turbo (not from turbo)
    if (!isTurboLike(autoPlaySpeed) || isTurboLike(prev)) return;

    // EOB collection: slam remaining coins
    const engine = spinEngineRef.current;
    if (engine && engine._endOfBonusActive) {

      engine.requestSlamStop();
    }

    // Entry coin spawn / feature activation: slam OrbActivator
    if (entryFeatureAnimatingRef.current && orbActivatorRef.current) {

      orbActivatorRef.current.requestSlamStop();
    }
  }, [potCollectionActive, autoPlaySpeed]);

  // ── OrbActivator Lifecycle ──
  useEffect(() => {
    const animator = potChargingAnimatorRef?.current;
    const trailContainer = animator?.trailContainer;
    const gameViewSpine = animator?.gameViewSpine;

    if (trailContainer && potCollectionActive) {
      if (!orbActivatorRef.current) {
        orbActivatorRef.current = new PotCollectionOrbActivator(
          trailContainer,
          gameViewSpine,
          symbolWidth,
          symbolHeight,
        );
      } else {
        orbActivatorRef.current.resize(symbolWidth, symbolHeight);
        if (gameViewSpine) orbActivatorRef.current.setSpine(gameViewSpine);
      }
    }

    return () => {
      if (orbActivatorRef.current && !potCollectionActive) {
        orbActivatorRef.current.destroy();
        orbActivatorRef.current = null;
      }
    };
  }, [potCollectionActive, symbolWidth, symbolHeight, potChargingAnimatorRef]);

  // ── Entry Phase: Coin Ejection ──
  // Called from entry sequence after transition midpoint reveals bonus grid.
  const runSpawnPhase = useCallback(async (speedMode = "normal") => {
    const state = store.getState();
    const initialReels = state.potCollection.initialRespinScreen;
    const respinGameType = state.potCollection.respinGameType;

    if (!initialReels || initialReels.length === 0 || !respinGameType) return;
    if (!orbActivatorRef.current) return;

    dispatch(setPotCollectionEntryPhase("spawn"));

    await orbActivatorRef.current.spawnCoins({
      respinGameType,
      initialReels,
      reelSpacing,
      overlayReelRefs: potCollectionOverlayReelRefs,
      reelRefs: potCollectionReelRefs,
      potControllers: gameViewControllerRef?.current?.potControllers,
      onCoinSpawned: ({ index }) => {
        dispatch(incrementPotCollectionCoinCount());
      },
      onAllSpawned: () => {
        dispatch(setPotCollectionEjectionComplete(true));
      },
      speedMode,
    });
  }, [dispatch, reelSpacing, potCollectionOverlayReelRefs, potCollectionReelRefs, gameViewControllerRef]);

  // ── Entry Phase: Orb Activation + Feature Execution ──
  // Called after ejection completes. For each feature in M→D→C:
  //   1. Bolt from POT → Zeus coin
  //   2. Zeus plays activate animation
  //   3. Feature executes using trigger spin's screen_progress (via ScreenProgressProcessor)
  // GDD §13.1 steps 5-7: sequential per-Zeus.
  const runActivationPhase = useCallback(async (slamStop = false) => {
    const state = store.getState();
    const activationQueue = state.potCollection.activationQueue;
    const initialReels = state.potCollection.initialRespinScreen;
    const triggerScreenProgress = state.potCollection.screenProgress;

    if (!activationQueue || activationQueue.length === 0) {
      return;
    }
    if (!orbActivatorRef.current) {
      return;
    }

    dispatch(setPotCollectionEntryPhase("activation"));

    // Create a ScreenProgressProcessor for the trigger spin's feature execution.
    // This reuses the same parsing + FeatureExecutor wiring used for respin responses.
    const collectionLayer = potCollectionCollectionLayerRef?.current;
    const collectionContainer = collectionLayer?.getContainer?.();
    const screenProgressProcessor = new PotCollectionScreenProgressProcessor({
      overlayReelRefs: potCollectionOverlayReelRefs,
      reelRefs: potCollectionReelRefs,
      collectionLayerContainer: collectionContainer,
      symbolWidth,
      symbolHeight,
      reelSpacing,
    });

    // Pre-warm the Double feature's pooled 2x label spine now (activation-phase
    // start), so the one-time Spine.from() cold-start is masked by the first
    // Mystery orb's bolt/flash instead of stuttering on the Double orb (iPhone 11).
    screenProgressProcessor.prewarm();

    // Track consumption cursors for screen_progress actions across activations.
    // Each Zeus activation consumes the NEXT action of each type (not all at once).
    // e.g. Double 1 gets d_doubles[0]+d_reveals[0], Double 2 gets d_doubles[1]+d_reveals[1].
    const actionCursors = {};

    await orbActivatorRef.current.activateOrbs({
      activationQueue,
      initialReels,
      reelSpacing,
      overlayReelRefs: potCollectionOverlayReelRefs,
      potControllers: gameViewControllerRef?.current?.potControllers,
      onActivated: ({ feature }) => {
        dispatch(setPotCollectionCurrentActivation(feature));
      },
      // After each Zeus activate animation, execute that feature's screen_progress actions.
      onFeatureReady: async ({ feature, reelIndex, symbolIndex, queueIndex, slamStop: reactiveSlamStop }) => {

        if (!triggerScreenProgress || triggerScreenProgress.length === 0) {
          return;
        }

        // Consume the next action of each type for this feature (cursor-based).
        // This ensures each Zeus gets exactly its own actions, not all same-type actions.
        const actionPrefixes = FEATURE_TO_ACTIONS[feature] || [];
        const featureActions = [];

        for (const prefix of actionPrefixes) {
          const cursor = actionCursors[prefix] || 0;
          let found = 0;
          for (const action of triggerScreenProgress) {
            if (action.action === prefix) {
              if (found === cursor) {
                featureActions.push(action);
                actionCursors[prefix] = cursor + 1;
                break;
              }
              found++;
            }
          }
        }

        if (featureActions.length === 0) {
          return;
        }

        // Build activatedZeusPosition so the ScreenProgressProcessor knows
        // which Zeus is the trail source (instead of scanning the grid).
        const activatedZeusPosition = (reelIndex !== undefined && symbolIndex !== undefined)
          ? { reelIndex, symbolIndex }
          : null;

        // Multi-double context: count total doubles in queue and this double's index
        let doubleContext = null;
        if (feature === "D") {
          const totalDoubles = activationQueue.filter(
            (e) => (typeof e === "string" ? e : e.feature) === "D"
          ).length;
          const doubleIndex = activationQueue
            .slice(0, queueIndex + 1)
            .filter((e) => (typeof e === "string" ? e : e.feature) === "D")
            .length - 1;
          doubleContext = { doubleIndex, totalDoubles };
        }

        // Pending Zeus positions: Zeus coins after this one in the queue that
        // haven't been activated yet. Prevents this Zeus from doubling/collecting them.
        const pendingZeusPositions = new Set();
        for (let pi = queueIndex + 1; pi < activationQueue.length; pi++) {
          const pending = activationQueue[pi];
          const pr = typeof pending === "string" ? undefined : pending.reelIndex;
          const ps = typeof pending === "string" ? undefined : pending.symbolIndex;
          if (pr !== undefined && ps !== undefined) {
            pendingZeusPositions.add(`${pr}_${ps}`);
          }
        }

        // Process this feature's actions — pass orbActivator as "spinEngine" so the
        // FeatureExecutor can reactively check _slamStopRequested during entry
        await screenProgressProcessor.processAll(
          featureActions, dispatch, slamStop || reactiveSlamStop,
          activatedZeusPosition, doubleContext, orbActivatorRef.current, pendingZeusPositions
        );

        // Auto speed: reset slam stop so next feature starts at normal speed.
        // User must slam again for each individual feature.
        // Turbo is unaffected — slamStop param is permanently true.
        if (!slamStop && orbActivatorRef.current?._slamStopRequested) {
          orbActivatorRef.current._slamStopRequested = false;
          entrySlamStopRef.current = false;
        }
      },
      onAllActivated: () => {
        dispatch(setPotCollectionFeatureApplicationComplete(true));
        dispatch(setPotCollectionEntryPhase("feature"));
      },
      slamStop,
      speedMode: slamStop ? "turbo" : "normal",
    });

    // Cleanup processor
    screenProgressProcessor.destroy();
  }, [dispatch, reelSpacing, symbolWidth, symbolHeight, potCollectionOverlayReelRefs, potCollectionReelRefs, potCollectionCollectionLayerRef, gameViewControllerRef]);

  // ── SpinEngine Lifecycle ──
  useEffect(() => {
    if (potCollectionActive && entryPhase === "ready") {
      if (!spinEngineRef.current) {
        spinEngineRef.current = new PotCollectionSpinEngine({
          reelRefs: potCollectionReelRefs,
          overlayReelRefs: potCollectionOverlayReelRefs,
          collectionLayerRef: potCollectionCollectionLayerRef,
          respinCounterRef: potCollectionRespinCounterRef,
          dispatch,
          onSpinComplete: () => {
            const bet = store.getState().game.bet || 1;
            dispatch(setFeatureSpinComplete({ complete: true, bet }));
            dispatch(setFeatureSpinActive(false));
          },
          onBonusEnd: (reason) => {
            dispatch(setPotCollectionEntryPhase("collection"));
          },
          gameViewControllerRef,
          potChargingAnimatorRef,
          symbolWidth,
          symbolHeight,
          reelSpacing,
        });
      } else {
        spinEngineRef.current.resize(symbolWidth, symbolHeight, reelSpacing);
      }
    }

    return () => {
      if (spinEngineRef.current && !potCollectionActive) {
        spinEngineRef.current.destroy();
        spinEngineRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    potCollectionActive,
    entryPhase,
    dispatch,
    symbolWidth,
    symbolHeight,
    reelSpacing,
    potCollectionReelRefs,
    potCollectionOverlayReelRefs,
    potCollectionCollectionLayerRef,
    gameViewControllerRef,
    potChargingAnimatorRef,
  ]);

  // ── Completion Reload: trigger EOB after reload popup dismissed ──
  // When reloadPhase is "collection", the bonus is finished but localStorage
  // still has data. After the "RESPINS LEFT" popup is shown and dismissed,
  // trigger the collection sequence (same natural flow as normal gameplay).
  // When respinsLeft === 0, the popup is skipped — auto-trigger EOB after 1s delay.
  const completionEobTriggeredRef = useRef(false);
  const reloadPopupWasShownRef = useRef(false);
  const gjpDelayTimerRef = useRef(null);
  const gjpPopupUnsubRef = useRef(null);

  // Helper: start the EOB collection sequence (shared by popup-dismiss and auto-start paths)
  const triggerCollectionEob = useCallback(() => {
    const engine = spinEngineRef.current;
    if (!engine || engine._endOfBonusActive) return;

    const pcState = store.getState().potCollection;
    const coins = pcState?.coinCount || 0;
    const TOTAL_CELLS = 15; // 5 reels × 3 rows
    const isGridFull = coins >= TOTAL_CELLS;

    // ── Max win check ──
    const totalWin = pcState?.currentRound?.win || 0;
    const bet = store.getState().game.bet || 1;
    const maxWinMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
    const isMaxWin = !!pcState?.winLimitReached || (bet > 0 && totalWin >= bet * maxWinMultiplier);

    if (isGridFull && !isMaxWin) {
      // ── GJP completion reload (no max win) ──
      const gvc = gameViewControllerRef?.current;
      const glowPromise = gvc?.playReelGlow
        ? gvc.playReelGlow()
        : Promise.resolve();
      engine._playGjpAnticipationWin();

      glowPromise.then(() => {
        gjpDelayTimerRef.current = new TickerTimeout(() => {
          gjpDelayTimerRef.current = null;
          dispatch(setShowPotCollectionGrandJackpot(true));
          gjpPopupUnsubRef.current = store.subscribe(() => {
            if (!store.getState().animations.showPotCollectionGrandJackpot) {
              if (gjpPopupUnsubRef.current) {
                gjpPopupUnsubRef.current();
                gjpPopupUnsubRef.current = null;
              }
              engine._startEndOfBonusSequence(true, false);
            }
          });
        }, 500);
      });
    } else {
      // ── All other completions (non-GJP, max win ± GJP) ──
      // Play bonus_end animation after short delay for intro popup to close.
      gjpDelayTimerRef.current = new TickerTimeout(() => {
        gjpDelayTimerRef.current = null;
        const gvc = gameViewControllerRef?.current;
        if (gvc?.playBonusEnd) {
          gvc.playBonusEnd();
        }
        engine._startEndOfBonusSequence(isGridFull, false);
      }, 500);
    }
  }, [dispatch, gameViewControllerRef]);

  useEffect(() => {
    if (reloadPhase !== "collection") return;

    // Track popup lifecycle: must have been shown first
    if (showReloadPopup) {
      reloadPopupWasShownRef.current = true;
      return;
    }

    // Gate: need active bonus, ready state, and spin engine
    if (!potCollectionActive || entryPhase !== "ready" || !spinEngineRef.current || completionEobTriggeredRef.current) {
      return;
    }

    // Path A: popup was shown and dismissed (true → false) — normal flow
    // Also wait for intro/info popups to close before triggering animations
    if (reloadPopupWasShownRef.current && !showReloadPopup && !introPopupActive && !infoPopupActive) {
      completionEobTriggeredRef.current = true;
      triggerCollectionEob();
      return;
    }

    // Path B: popup was skipped (respinsLeft === 0) — wait for all popups to close
    // Reactive: effect re-runs when introPopupActive/infoPopupActive change
    if (respinsLeft === 0 && !reloadPopupWasShownRef.current && !introPopupActive && !infoPopupActive) {
      completionEobTriggeredRef.current = true;
      triggerCollectionEob();
    }

    return () => {
      // Cleanup: clear pending timers and unsubscribe if component unmounts mid-sequence
      if (gjpDelayTimerRef.current) {
        if (gjpDelayTimerRef.current && typeof gjpDelayTimerRef.current.clear === "function") gjpDelayTimerRef.current.clear();
        gjpDelayTimerRef.current = null;
      }
      if (gjpPopupUnsubRef.current) {
        gjpPopupUnsubRef.current();
        gjpPopupUnsubRef.current = null;
      }
    };
  }, [reloadPhase, potCollectionActive, entryPhase, showReloadPopup, respinsLeft, introPopupActive, infoPopupActive, dispatch, gameViewControllerRef, triggerCollectionEob]);

  // ── Entry Sequence Orchestration ──
  // When potCollectionActive transitions to true and entryPhase is "popup"
  // (set during trigger detection in potCollectionSlice), run the full entry
  // sequence: ejection → activation → ready → first spin.
  // IMPORTANT: entrySequenceCompletedRef stays true for the ENTIRE bonus lifetime.
  // This ensures spawnCoins + activateOrbs are ONE AND DONE — even if the effect
  // re-fires due to dep changes or entryPhase briefly flipping back to "popup".
  const entrySequenceRunningRef = useRef(false);
  const entrySequenceCompletedRef = useRef(false);

  useEffect(() => {
    if (
      potCollectionActive &&
      entryPhase === "popup" &&
      !entrySequenceRunningRef.current &&
      !entrySequenceCompletedRef.current
    ) {
      entrySequenceRunningRef.current = true;

      // Mark entry as animating IMMEDIATELY so button routes spacebar
      // to slam stop throughout the entire entry sequence.
      entryFeatureAnimatingRef.current = true;
      entrySlamStopRef.current = false;
      // Signal that we're waiting for transition to finish before spawning
      entryWaitingForTransitionRef.current = true;
    }

    // Reset when deactivated (bonus ended — allow fresh entry on next trigger)
    if (!potCollectionActive) {
      entrySequenceRunningRef.current = false;
      entrySequenceCompletedRef.current = false;
      entrySlamStopRef.current = false;
      entryWaitingForTransitionRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [potCollectionActive, entryPhase]);

  // ── Entry Phase Runner — fires when transition completes ──
  // Watches transitionActive going false while entry is waiting.
  // At this point, clouds are gone and React has mounted all PC components.
  useEffect(() => {
    if (
      !transitionActive &&
      entryWaitingForTransitionRef.current &&
      entrySequenceRunningRef.current &&
      !entrySequenceCompletedRef.current
    ) {
      entryWaitingForTransitionRef.current = false;

      (async () => {
        try {
          // Speed is already forced to 'auto' at transition midpoint (GameView.jsx).
          // Entry always runs at normal speed.
          const speedMode = "normal";

          // Play respin counter 0→3 animation (bonus is now visible)
          potCollectionRespinCounterRef?.current?.showFull();

          // Phase 1: Coin ejection (POT → Grid)
          // FEATURE_SPEED.ENTRY_SPAWN_TURBO controls default; entrySlamStopRef is legacy fallback
          const entryTurbo = FEATURES_CONFIG.POT_COLLECTION.FEATURE_SPEED?.ENTRY_SPAWN_TURBO || entrySlamStopRef.current;
          await runSpawnPhase(entryTurbo ? "turbo" : speedMode);

          // Reset slam stop flags after spawn — don't carry over to activation phase.
          // Spawn slam stop should only speed up coin ejection, not mystery shuffle etc.
          entrySlamStopRef.current = false;
          if (orbActivatorRef.current) {
            orbActivatorRef.current._slamStopRequested = false;
          }

          // Phase 2: Orb activation + feature execution (POT → Zeus → M→D→C)
          // Always normal speed at bonus start. Slam stop still works per-feature.
          await runActivationPhase(false);

          // Entry features done
          entryFeatureAnimatingRef.current = false;

          // Phase 3: Mark ready — SpinEngine will auto-init via the effect above
          dispatch(setPotCollectionEntryPhase("ready"));

          // Unlock UI — entry sequence complete, user can now interact
          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setUserInterfaceDisabled(false));

          // Signal readiness — featureSpinComplete=true enables the play button
          const bet = store.getState().game.bet || 1;
          dispatch(setFeatureSpinComplete({ complete: true, bet }));
        } catch (error) {

          dispatch(setPotCollectionEntryPhase("ready"));
          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setUserInterfaceDisabled(false));
          dispatch(setPotCollectionTransitionActive(false));
          const bet = store.getState().game.bet || 1;
          dispatch(setFeatureSpinComplete({ complete: true, bet }));
        }
        // Mark permanently completed — spawnCoins + activateOrbs never run again
        entrySequenceCompletedRef.current = true;
        entrySequenceRunningRef.current = false;
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionActive, runSpawnPhase, runActivationPhase, dispatch]);

  // ── Exit Sequence Orchestration ──
  // When entryPhase transitions to "collection", the spin loop is done (respins exhausted).
  // Run EOB engine → total win popup → transition back to base game.
  const exitSequenceRunningRef = useRef(false);

  useEffect(() => {
    if (
      potCollectionActive &&
      entryPhase === "collection" &&
      !exitSequenceRunningRef.current
    ) {
      exitSequenceRunningRef.current = true;

      const runExitSequence = async () => {
        try {
          // NOTE: Value collection was already completed by SpinEngine's
          // _startEndOfBonusSequence → EndOfBonusEngine.runCollection().
          // This exit sequence only handles the transition back to base game.

          // Transition back to base game via GVC
          const gvc = gameViewControllerRef?.current;
          if (gvc) {
            await new Promise((resolve) => {
              gvc.playTransition("main", () => {
                // ── MIDPOINT: screen covered ──

                // Restore player's pre-bonus speed from localStorage.
                // Bonus entry used setAutoplaySpeedNoSave so the persisted value is intact.
                const savedUISettings = StorageManager.loadUISettings();
                const savedSpeed = savedUISettings.autoPlaySpeed || 'auto';
                const liveSpeed = store.getState().settings.autoPlaySpeed;
                if (liveSpeed !== savedSpeed) {
                  dispatch(setAutoplaySpeedNoSave(savedSpeed));
                }

                dispatch(setPotCollectionActive(false));
                dispatch(clearFeatureStateToBase());

                // Reset spin state so base game button works
                dispatch(setFeatureSpinComplete(true));

                // BLOCK UI: Keep interface locked during transition + balance sync.
                // ButtonPlay re-mounts when potCollectionActive goes false, so we must
                // ensure these guards are explicitly true to prevent spacebar/click from
                // triggering a spin with stale balance.
                dispatch(setCriticalAnimationPlaying(true));
                dispatch(setUserInterfaceDisabled(true));

                // Reset triggered POTs to stage 0 (GDD §12.2 step 7)
                const currentState = store.getState();
                const respinGameType = currentState.potCollection?.respinGameType || "";
                const typeToTier = { M: "mystery", D: "double", C: "collect" };
                for (const [typeChar, tier] of Object.entries(typeToTier)) {
                  if (respinGameType.includes(typeChar) && gvc.potControllers[tier]) {
                    gvc.potControllers[tier].setStage(0);
                  }
                }

                // Destroy coin counter
                gvc.destroyCoinCounter();

                // Fade music back
                const { soundManager } = require("../SoundManager");
                const { SOUND_NAME } = require("../sounds");
                soundManager.currentBackgroundMusic = SOUND_NAME.background_music;
                soundManager.fadeIn(SOUND_NAME.background_music, 500);
              });

              // Wait for transition_active to clear
              // eslint-disable-next-line no-unused-vars
              let check = null;
              const poll = () => {
                if (!gvc._transitionActive) {
                  resolve();
                } else {
                  check = new TickerTimeout(poll, 100);
                }
              };
              // eslint-disable-next-line no-unused-vars
              check = new TickerTimeout(poll, 100);
            });

            // Transition complete — refresh game data (balance sync) then re-enable UI
            await dispatch(refreshGameData());
            dispatch(setCriticalAnimationPlaying(false));
            dispatch(setUserInterfaceDisabled(false));
          } else {
            // Fallback: no GVC
            dispatch(setPotCollectionActive(false));
            dispatch(clearFeatureStateToBase());
            dispatch(refreshGameData());
          }
        } catch (error) {
          // Safety: deactivate even on error
          dispatch(setPotCollectionActive(false));
          dispatch(clearFeatureStateToBase());
        }
        exitSequenceRunningRef.current = false;
      };

      runExitSequence();
    }

    if (!potCollectionActive) {
      exitSequenceRunningRef.current = false;
    }
  }, [
    potCollectionActive,
    entryPhase,
    dispatch,
    potCollectionReelRefs,
    potCollectionOverlayReelRefs,
    potCollectionCollectionLayerRef,
    potCollectionRespinCounterRef,
    potCollectionCoinCounterRef,
    gameViewControllerRef,
    symbolWidth,
    symbolHeight,
    reelSpacing,
  ]);

  // ── Autoplay — trigger first spin when entry sequence completes ──
  // Watches featureSpinComplete: when it becomes true and respins remain,
  // the bonus is ready for its first spin. Subsequent spins are scheduled
  // by onSpinComplete above.
  // GATE: Requires autoplayStartedRef to be true (user must click play first).
  // While waiting, a pulse glow animation plays on the play button.
  useEffect(() => {
    // Hard block: while reload popup, error popup, or error recovery overlay is active,
    // NO spin triggers whatsoever. errorRecoveryTransitionActive is needed because
    // WarningPopup batches closeErrorPopup + setSpinComplete in the same callback,
    // so errorPopupActive is already false by the time this effect re-runs.
    if (showReloadPopup || errorPopupActive || errorRecoveryTransitionActive) return;

    // A respin was rejected: this tab's state disagrees with the server, so the
    // loop must not schedule anything until a re-sync lands. The rgsPlay
    // condition guard would refuse the request anyway — stopping here also
    // avoids pretending to spin and desyncing the visuals.
    if (store.getState().spin.respinBlockedUntilResync) return;

    if (
      potCollectionActive &&
      entryPhase === "ready" &&
      featureSpinComplete &&
      !featureSpinActive &&
      respinsLeft > 0 &&
      spinEngineRef.current &&
      !spinEngineRef.current._isSpinning &&
      !spinEngineRef.current._endOfBonusActive
    ) {
      // Reset loop flag after each spin so next one can be scheduled
      // (autoplayStartedRef stays true — user only clicks once)
      if (autoplayStartedRef.current && spinLoopStartedRef.current) {
        spinLoopStartedRef.current = false;
      }

      if (!autoplayStartedRef.current) {
        // ── Waiting for user click: start pulse glow on play button ──
        if (!pulseIntervalRef.current && playButtonRef.current) {
          const sprite = playButtonRef.current;
          // Create a brightness filter for the pulse
          const pulseFilter = new (require('pixi.js').ColorMatrixFilter)();
          pulseFilter.padding = 8;
          sprite.filters = [pulseFilter];

          // Proxy object for anime to tween the brightness value
          const proxy = { brightness: 1.0 };
          pulseIntervalRef.current = anime({
            targets: proxy,
            brightness: 2.5,
            duration: 300,
            easing: 'easeInOutSine',
            direction: 'alternate',
            loop: true,
            update: () => {
              if (!sprite || sprite.destroyed) {
                pulseIntervalRef.current?.pause();
                pulseIntervalRef.current = null;
                return;
              }
              // Re-ensure filter is attached — hover handlers may wipe sprite.filters
              if (!sprite.filters || !sprite.filters.includes(pulseFilter)) {
                sprite.filters = [pulseFilter];
              }
              pulseFilter.brightness(proxy.brightness, false);
            },
          });
        }
        return; // Don't auto-start — wait for user click
      }

      if (!autoplayTimeoutRef.current && !spinLoopStartedRef.current) {
        spinLoopStartedRef.current = true;
        const INTER_SPIN_DELAY = 150;
        autoplayTimeoutRef.current = new TickerTimeout(() => {
          autoplayTimeoutRef.current = null;
          if (playButtonRef.current) {
            createPlayButtonRotationAnimation(playButtonRef.current);
          }
          spinEngineRef.current?.handleSpin();
        }, INTER_SPIN_DELAY);
      }
    }
  }, [potCollectionActive, entryPhase, featureSpinComplete, featureSpinActive, respinsLeft, playButtonRef, showReloadPopup, errorPopupActive, errorRecoveryTransitionActive]);

  // ── Autoplay cleanup on bonus exit ──
  useEffect(() => {
    if (!potCollectionActive && autoplayTimeoutRef.current) {
      if (autoplayTimeoutRef.current && typeof autoplayTimeoutRef.current.clear === "function") autoplayTimeoutRef.current.clear();
      autoplayTimeoutRef.current = null;
    }
    if (!potCollectionActive) {
      spinLoopStartedRef.current = false;
      autoplayStartedRef.current = false;
      // Clear pulse animation
      if (pulseIntervalRef.current) {
        pulseIntervalRef.current.pause();
        pulseIntervalRef.current = null;
      }
      // Kill all anime.js tweens on the play button sprite (rotation, scale, etc.)
      // BEFORE React unmounts ButtonPotCollectionPlay and destroys the sprite.
      // Without this, in-flight rotation phases continue ticking after the
      // sprite is destroyed → "Cannot set properties of null (setting 'rotation')".
      if (playButtonRef.current) {
        anime.remove(playButtonRef.current);
        anime.remove(playButtonRef.current.scale);
        playButtonRef.current.filters = [];
      }
    }
  }, [potCollectionActive, playButtonRef]);

  // ── App Activity Change Effect (tab switch) ──
  // Mirrors ReelControlContext: with ticker-based timing, pause/resume is
  // AUTOMATIC. AppActivityContext sets Ticker.shared.speed = 0 → deltaMS = 0
  // for symbol spins, TickerTimeouts (autoplay delay, stop cascade), etc.
  // Do NOT clear autoplay timeouts or manually pause controllers — that
  // destroyed in-flight schedule state and left spinLoopStartedRef stuck.
  useEffect(() => {
    if (!potCollectionActive || entryPhase !== "ready") return;

    if (!isActive) {
      pausedByInactivityRef.current = true;
      return;
    }

    if (!pausedByInactivityRef.current) return;

    const now = Date.now();
    if (lastTabReturnTimeRef.current && now - lastTabReturnTimeRef.current < 500) {
      return;
    }
    lastTabReturnTimeRef.current = now;
    pausedByInactivityRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, potCollectionActive, entryPhase]);

  // ── Spin Control Wrappers (used by ButtonPotCollectionPlay) ──

  const handleManualStop = useCallback(() => {
    if (spinEngineRef.current) {
      spinEngineRef.current.handleManualStop();
    }
  }, []);

  const requestSlamStop = useCallback(() => {
    if (spinEngineRef.current) {
      spinEngineRef.current.requestSlamStop();
    }
    // During entry sequence: signal orbActivator to speed up (not cancel)
    if (entryFeatureAnimatingRef.current) {
      entrySlamStopRef.current = true;
      if (orbActivatorRef.current) {
        orbActivatorRef.current.requestSlamStop();
      }
    }
  }, []);

  const isFeatureAnimating = useCallback(() => {
    // Check both SpinEngine (normal spins) and entry-phase ref
    return (spinEngineRef.current?._featureAnimating ?? false) || entryFeatureAnimatingRef.current;
  }, []);

  const isEndOfBonusActive = useCallback(() => {
    return spinEngineRef.current?._endOfBonusActive ?? false;
  }, []);

  // ── Start Autoplay (called by ButtonPotCollectionPlay on first click) ──
  const startAutoplay = useCallback(() => {
    // Block while reload popup is active
    if (store.getState().animations.showPotCollectionReload) return;
    // Bonus finished — no more spins, only EOB slam-stop is allowed via requestSlamStop
    if ((store.getState().potCollection?.respinsLeft ?? 0) <= 0) {
      return;
    }
    if (autoplayStartedRef.current) return;
    autoplayStartedRef.current = true;

    // Stop pulse animation
    if (pulseIntervalRef.current) {
      pulseIntervalRef.current.pause();
      pulseIntervalRef.current = null;
    }
    if (playButtonRef.current) {
      playButtonRef.current.filters = [];
    }

    // Fire first spin directly — can't rely on the React effect re-triggering
    // because refs don't cause re-renders. Same logic as the auto-trigger effect.
    if (
      spinEngineRef.current &&
      !spinEngineRef.current._isSpinning &&
      !spinEngineRef.current._endOfBonusActive &&
      !spinLoopStartedRef.current
    ) {
      spinLoopStartedRef.current = true;
      const INTER_SPIN_DELAY = 150;
      autoplayTimeoutRef.current = new TickerTimeout(() => {
        autoplayTimeoutRef.current = null;
        if (playButtonRef.current) {
          createPlayButtonRotationAnimation(playButtonRef.current);
        }
        spinEngineRef.current?.handleSpin();
      }, INTER_SPIN_DELAY);
    }
  }, [playButtonRef]);

  // ── Coin Counter Management ──
  // Counter is pre-initialized at game load (0/15). Update value when PC activates or coinCount changes.
  useEffect(() => {
    const gvc = gameViewControllerRef?.current;

    if (!gvc) return;

    if (potCollectionActive) {
      gvc.updateCoinCounter(coinCount, 15);
      gvc.setCoinCounterVisible(true);
    } else {
      gvc.setCoinCounterVisible(false);
      gvc.updateCoinCounter(0, 15); // Reset for next trigger
    }
  }, [potCollectionActive, coinCount, gameViewControllerRef]);

  // ── Context Value ──
  // Shell exposes refs and entry flow orchestration.
  // Spin scheduling will be event-driven (added later).
  const contextValue = useMemo(
    () => ({
      // Refs
      potCollectionReelRefs,
      potCollectionOverlayReelRefs,
      potCollectionCollectionLayerRef,
      potCollectionRespinCounterRef,
      potCollectionCoinCounterRef,

      // Spin state refs
      isSpinningRef,
      spinningCountRef,
      completedCountRef,
      coinLandedThisSpinRef,
      componentsEverReadyRef,

      // Entry flow orchestration
      runSpawnPhase,
      runActivationPhase,
      orbActivatorRef,

      // Spin engine
      spinEngineRef,

      // Spin control (used by ButtonPotCollectionPlay — stop/slam only, autoplay drives spins)
      handleManualStop,
      requestSlamStop,
      isFeatureAnimating,
      isEndOfBonusActive,
      startAutoplay,
      autoplayStartedRef,
    }),
    [
      potCollectionReelRefs,
      potCollectionOverlayReelRefs,
      potCollectionCollectionLayerRef,
      potCollectionRespinCounterRef,
      potCollectionCoinCounterRef,
      runSpawnPhase,
      runActivationPhase,

      handleManualStop,
      requestSlamStop,
      isFeatureAnimating,
      isEndOfBonusActive,
      startAutoplay,
    ],
  );

  return (
    <PotCollectionControlContext.Provider value={contextValue}>
      {children}
    </PotCollectionControlContext.Provider>
  );
};

export const usePotCollectionControl = () => {
  const ctx = useContext(PotCollectionControlContext);
  if (!ctx) {
    throw new Error(
      "usePotCollectionControl must be used within PotCollectionControlContextProvider",
    );
  }
  return ctx;
};
